/**
 * Store pour la gestion de l'application
 */

import { ref, computed } from 'vue';
import { defineStore } from 'pinia';
import axios from '@js/tools/axios';

export const useApplicationStore = defineStore('application', (() => {

    // State des langues disponibles
    const enabledLanguages = ref(window.AppConfig.enabledLanguages);

    // State des réseaux sociaux disponibles
    const enabledSocialNetworks = ref(window.AppConfig.enabledSocialNetworks);

    // State si l'édition de template de charte graphique est active
    const graphicalLineTemplateEnabled = ref(window.AppConfig.withGraphicalLineTemplate);

    // State si plusieurs langues sont utilisables sur l'interface
    const multiLanguagesEnabled = ref(window.AppConfig.withMultiLanguages);

    // State de l'application courante (domaine où on se trouve)
    const current = ref(null);

    // State de l'application de l'utilisateur connecté
    const me = ref(null);

    // Retourne si l'application en paramètre est l'application courante
    const isCurrent = computed(() => {
        return ((application) => {
            if(current.value === null || application === null) {
                return false;
            }

            return (application.domain === current.value.domain);
        });
    });

    // Retourne les langues disponibles
    const getEnabledLanguages = computed(() => enabledLanguages.value);

    // Retourne les réseaux sociaux disponibles
    const getEnabledSocialNetworks = computed(() => enabledSocialNetworks.value);

    // Retourne si les templates de chartes graphiques sont activés
    const getWithGraphicalLineTemplate = computed(() => graphicalLineTemplateEnabled.value);

    // Retourne si plusieurs languages sont utilisables sur l'interface
    const getWithMultiLanguagesEnabled = computed(() => multiLanguagesEnabled.value);

    /**
     * Retourne l'application courante
     * @return object|null
     */
    const getCurrent = computed(() => current.value);

    /**
     * Retourne l'application de l'utilisateur
     * @return object|null
     */
    const getMe = computed(() => me.value);

    /**
     * Récupère les données de l'application courante (nom, domaine)
     * @return Promise
     */
    const loadCurrent = (() => {

        if(current.value !== null) {
            return Promise.resolve();
        }

        return axios.get('/api/applications/current').then((response) => {
            if(response.data.success && response.data.application) {
                setCurrent(response.data.application);
                return Promise.resolve();
            }
        });

    });

    /**
     * Récupère les données de l'application de l'utilisateur connecté (nom, domaine, fichiers)
     * @return Promise
     */
    const loadMe = (() => {

        if(me.value !== null) {
            return Promise.resolve();
        }

        return axios.get('/api/admin/applications/me').then((response) => {
            if(response.data.success && response.data.application) {
                setMe(response.data.application);
                return Promise.resolve();
            }
        });

    });

    /**
     * Modifie l'application courante
     * @param object application
     * @return void
     */
    const setCurrent = ((application) => {
        current.value = application;
    });

    /**
     * Modifie l'application de l'utilisateur connecté
     * @param object application
     * @return void
     */
    const setMe = ((application) => {
        me.value = application;
    });

    return {
        isCurrent,
        getCurrent,
        getEnabledLanguages,
        getEnabledSocialNetworks,
        getWithGraphicalLineTemplate,
        getWithMultiLanguagesEnabled,
        getMe,
        loadCurrent,
        loadMe,
        setMe
    };

}));
